import { usePairs } from '@/store/pairs';
import { usePriceByPairIndex } from '@/store/selector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

import { FormValue, Type } from '../type';
import { t } from 'i18next';

import { calculateTPSLV2 } from './calculateTPSLV2';

export const useTPSLV2 = () => {
    const { control, setValue } = useFormContext<FormValue>();

    const [amount, pairIndex, type, leverage, tp, sl] = useWatch({
        control,
        name: ['amount', 'pairIndex', 'type', 'leverage', 'tp', 'sl'],
    });
    const { price } = usePriceByPairIndex(pairIndex);
    const typeTrade = type === Type.Long ? 'Long' : 'Short';

    const pairs = usePairs(useShallow((state) => state.data));
    const pairInfo = useMemo(
        () => pairs.find((p) => p.pairIndex === Number(pairIndex)),
        [pairs, pairIndex]
    );
    const decimalsToShow = pairInfo?.decimalsToShow ?? 6;

    const setValueRef = useRef(setValue);
    setValueRef.current = setValue;

    // Sync ref so callbacks always read latest values without being recreated
    const calcRef = useRef<{
        price: string;
        amount: string | number;
        leverage: number;
        typeTrade: 'Long' | 'Short';
        decimalsToShow: number;
    }>({ price, amount, leverage, typeTrade, decimalsToShow });
    calcRef.current = { price, amount, leverage, typeTrade, decimalsToShow };

    const [takeProfitPrice, setTakeProfitPrice] = useState<number>();
    const [stopLossPrice, setStopLossPrice] = useState<number>();
    const [liqPrice, setLiqPrice] = useState<number>();
    const [takeProfitPercent, setTakeProfitPercent] = useState<number>();
    const [stopLossPercent, setStopLossPercent] = useState<number>();

    // Handlers below already compute price/percent from the user's exact raw input and
    // write the result straight into local state; they set these refs right before writing
    // `tp`/`sl` back to the form so the sync effects (which re-derive price from the
    // *rounded* percent) skip that one round-trip instead of snapping the displayed price
    // to a slightly different rounded value while the user is mid-type.
    const skipNextTpSync = useRef(false);
    const skipNextSlSync = useRef(false);

    // Recompute TP price/percent from the form's `tp` (source of truth) whenever it's
    // set externally (e.g. pre-filled via query params) or leverage/price/amount change —
    // the ROI% target stays fixed, but the price needed to hit it depends on leverage.
    useEffect(() => {
        if (skipNextTpSync.current) {
            skipNextTpSync.current = false;
            return;
        }
        if (tp === undefined || tp === '') {
            setTakeProfitPrice(undefined);
            setTakeProfitPercent(undefined);
            return;
        }
        const { tpPercent, tpPrice, liqPrice: liq } = calculateTPSLV2({
            entryPrice: Number(price),
            typeTrade,
            amount: Number(amount),
            leverage,
            decimalsToShow,
            tpPercent: Number(tp),
        });
        setTakeProfitPrice(tpPrice);
        setTakeProfitPercent(tpPercent);
        setLiqPrice(liq);
    }, [tp, leverage, price, amount, typeTrade, decimalsToShow]);

    useEffect(() => {
        if (skipNextSlSync.current) {
            skipNextSlSync.current = false;
            return;
        }
        if (sl === undefined || sl === '') {
            setStopLossPrice(undefined);
            setStopLossPercent(undefined);
            return;
        }
        const { slPercent, slPrice, liqPrice: liq } = calculateTPSLV2({
            entryPrice: Number(price),
            typeTrade,
            amount: Number(amount),
            leverage,
            decimalsToShow,
            slPercent: Number(sl),
        });
        setStopLossPrice(slPrice);
        setStopLossPercent(slPercent);
        setLiqPrice(liq);
    }, [sl, leverage, price, amount, typeTrade, decimalsToShow]);

    const handleSetTpPrice = useCallback((value: string) => {
        if (value.length > 18) return;
        const { price, amount, leverage, typeTrade, decimalsToShow } = calcRef.current;
        const inputValue = value.replace(',', '.');
        const { tpPercent, tpPrice, liqPrice: liq } = calculateTPSLV2({
            entryPrice: Number(price),
            typeTrade,
            amount: Number(amount),
            leverage,
            decimalsToShow,
            tpPrice: Number(inputValue),
        });
        setTakeProfitPrice(tpPrice);
        setTakeProfitPercent(tpPercent);
        setLiqPrice(liq);
        skipNextTpSync.current = true;
        setValueRef.current('tp', tpPercent != null ? String(tpPercent) : '');
    }, []);

    const handleSetSlPrice = useCallback((value: string) => {
        if (value.length > 18) return;
        const { price, amount, leverage, typeTrade, decimalsToShow } = calcRef.current;
        const inputValue = value.replace(',', '.');
        const { slPercent, slPrice, liqPrice: liq } = calculateTPSLV2({
            entryPrice: Number(price),
            typeTrade,
            amount: Number(amount),
            leverage,
            decimalsToShow,
            slPrice: Number(inputValue),
        });
        setStopLossPrice(slPrice);
        setStopLossPercent(slPercent);
        setLiqPrice(liq);
        skipNextSlSync.current = true;
        setValueRef.current('sl', slPercent != null ? String(slPercent) : '');
    }, []);

    const handleClear = useCallback((type: 'tp' | 'sl') => {
        if (type === 'tp') {
            setValueRef.current('tp', '');
            setTakeProfitPrice(undefined);
            setTakeProfitPercent(undefined);
        }
        if (type === 'sl') {
            setValueRef.current('sl', '');
            setStopLossPrice(undefined);
            setStopLossPercent(undefined);
        }
    }, []);

    const handleTakeProfitPercent = useCallback((tpP: number) => {
        const { price, amount, leverage, typeTrade, decimalsToShow } = calcRef.current;
        const { tpPercent, tpPrice, liqPrice: liq } = calculateTPSLV2({
            entryPrice: Number(price),
            typeTrade,
            leverage,
            decimalsToShow,
            tpPercent: Number(tpP),
            amount: Number(amount),
        });
        setTakeProfitPrice(tpPrice);
        setTakeProfitPercent(tpPercent);
        setLiqPrice(liq);
        skipNextTpSync.current = true;
        setValueRef.current('tp', tpPercent != null ? String(tpPercent) : '');
    }, []);

    const handleStopLossPercent = useCallback((slP: number) => {
        const { price, amount, leverage, typeTrade, decimalsToShow } = calcRef.current;
        const { slPrice, slPercent, liqPrice: liq } = calculateTPSLV2({
            entryPrice: Number(price),
            typeTrade,
            leverage,
            decimalsToShow,
            slPercent: Number(slP),
            amount: Number(amount),
        });
        setStopLossPrice(slPrice);
        setStopLossPercent(slPercent);
        setLiqPrice(liq);
        skipNextSlSync.current = true;
        setValueRef.current('sl', slPercent != null ? String(slPercent) : '');
    }, []);

    // Computed synchronously — no useEffect + setState cycle needed
    // Unified sign convention: positive percent = profit (TP), negative = loss (SL) for both sides.
    const errorTP = useMemo(() => {
        const isLong = type === Type.Long;
        if (takeProfitPrice === 0)
            return t('Take profit must be above 0' );
        if (typeof takeProfitPercent !== 'number') return '';
        if (takeProfitPercent <= 0)
            return t(isLong ? 'Take profit must be above entry price' : 'Take profit must be below entry price');
        if (takeProfitPercent > 900) return t('Take profit cannot exceed 900%');
        return '';
    }, [takeProfitPercent, takeProfitPrice, type]);

    const errorSl = useMemo(() => {
        const isLong = type === Type.Long;
        if (stopLossPrice === 0)
            return t(isLong ? 'Stop loss must be above 0' : 'Stop loss must be above entry price');
        if (typeof stopLossPercent !== 'number') return '';
        if (stopLossPercent >= 0)
            return t(isLong ? 'Stop loss must be below entry price' : 'Stop loss must be above entry price');
        if (Math.abs(stopLossPercent) > 100) return t('SL cannot exceed 100%');
        if (
            liqPrice != null &&
            stopLossPrice != null &&
            (isLong ? stopLossPrice <= liqPrice : stopLossPrice >= liqPrice)
        ) return t('Stop loss will trigger liquidation (liq price: {{liq}})', { liq: liqPrice });
        return '';
    }, [stopLossPercent, stopLossPrice, liqPrice, type]);

    return {
        errorTP,
        errorSl,
        takeProfitPrice,
        stopLossPrice,
        liqPrice,

        handleSetTpPrice,
        handleSetSlPrice,
        handleClear,

        takeProfitPercent,
        stopLossPercent,
        setTakeProfitPercent: handleTakeProfitPercent,
        setStopLossPercent: handleStopLossPercent,
    };
};
